import { ShieldCheck, AlertTriangle } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { SectionHeading } from "@/components/common/SectionHeading";
import { ProcessTimeline } from "@/components/common/ProcessTimeline";
import { Reveal } from "@/components/common/Reveal";
import { CTASection } from "@/components/common/CTASection";
import { StatusBadge } from "@/components/common/StatusBadge";
import { CASHOUT_STEPS, MONEY_MESSAGE, PAYOUT_METHODS } from "@/data/content";

export default function Cashouts() {
  return (
    <div data-testid="cashouts-page">
      <PageHeader
        overline="How Cashouts Work"
        titleLines={["Clear cashouts.", "Human approval."]}
        lead="Submit a request, upload proof, and get a transparent status update. No automatic money movement — ever."
        testid="cashouts-header"
      />

      <section className="px-5 py-14 md:px-8 md:py-20">
        <div className="mx-auto max-w-7xl">
          <SectionHeading overline="The process" title="Five steps to a reviewed cashout." />
          <div className="mt-16">
            <ProcessTimeline steps={CASHOUT_STEPS} highlightIndex={3} testid="cashouts-timeline" />
          </div>
        </div>
      </section>

      {/* non-negotiable money message */}
      <section className="px-5 pb-6 md:px-8">
        <Reveal className="mx-auto max-w-4xl overflow-hidden rounded-3xl border border-ff-ember/30 bg-ff-slate/70 p-8 backdrop-blur-xl md:p-12" data-testid="money-message">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-ff-ember/15 text-ff-ember">
              <AlertTriangle className="h-6 w-6" />
            </span>
            <StatusBadge tone="ember">Non-negotiable</StatusBadge>
          </div>
          <p className="mt-6 font-display text-xl font-semibold leading-snug text-ff-ivory md:text-3xl text-balance">
            {MONEY_MESSAGE}
          </p>
        </Reveal>
      </section>

      {/* payout methods */}
      <section className="px-5 py-16 md:px-8 md:py-24">
        <div className="mx-auto max-w-7xl">
          <SectionHeading
            overline="Payout methods"
            title="Supported where available."
            body="Payout options are rolled out by region. Tell us your country in your application and we'll confirm what's available to you."
          />
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {PAYOUT_METHODS.map((m, i) => (
              <Reveal key={m.name} delay={i * 0.06}>
                <div className="flex h-full items-center justify-between rounded-2xl border border-white/5 bg-ff-slate/60 p-6" data-testid={`payout-${m.name.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div>
                    <p className="font-display text-lg font-semibold text-ff-ivory">{m.name}</p>
                    <p className="mt-1 text-xs text-ff-grey">{m.note}</p>
                  </div>
                  <ShieldCheck className="h-5 w-5 text-ff-teal/70" />
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-8">
            <p className="text-sm text-ff-grey/80">
              Note: payout methods listed are planned or supported where available. Availability depends
              on your country and verification. Payouts are not guaranteed.
            </p>
          </Reveal>
        </div>
      </section>

      <CTASection title="Questions about payouts?" secondaryLabel="Read the FAQ" secondaryTo="/faq" />
    </div>
  );
}
